import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Breadcrumbs - Navigation breadcrumb component
 *
 * @param {Array} items - Array of breadcrumb items
 *   Each item: { label: string, path?: string }
 *   Last item should not have a path (current page)
 *
 * Example:
 *   <Breadcrumbs items={[
 *     { label: 'Dashboard', path: '/admin' },
 *     { label: 'Settings', path: '/admin/settings-hub' },
 *     { label: 'Branding' }
 *   ]} />
 */
function Breadcrumbs({ items = [] }) {
  if (items.length === 0) return null;

  return (
    <nav className="breadcrumbs" aria-label="Breadcrumb">
      {items.map((item, index) => {
        const isLast = index === items.length - 1;

        return (
          <React.Fragment key={index}>
            {index > 0 && (
              <span className="breadcrumb-separator" aria-hidden="true">
                /
              </span>
            )}
            {isLast || !item.path ? (
              <span
                className="breadcrumb-current"
                aria-current={isLast ? 'page' : undefined}
              >
                {item.label}
              </span>
            ) : (
              <Link to={item.path} className="breadcrumb-item">
                {item.label}
              </Link>
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );
}

// Pre-defined breadcrumb paths for common admin pages
export const BREADCRUMB_PATHS = {
  dashboard: { label: 'Dashboard', path: '/admin' },
  settingsHub: { label: 'Settings', path: '/admin/settings-hub' },
  propertiesHub: { label: 'Properties', path: '/admin/properties-hub' },
  customersLoansHub: { label: 'Customers & Loans', path: '/admin/customers-loans-hub' },
  toolsHub: { label: 'Tools', path: '/admin/tools-hub' },
  reportsHub: { label: 'Reports', path: '/admin/reports-hub' },
  accountHub: { label: 'Account', path: '/admin/account-hub' }
};

// Helper to build breadcrumbs for settings pages
export function buildSettingsBreadcrumbs(currentPageLabel) {
  return [
    BREADCRUMB_PATHS.dashboard,
    BREADCRUMB_PATHS.settingsHub,
    { label: currentPageLabel }
  ];
}

// Helper to build breadcrumbs for tools pages
export function buildToolsBreadcrumbs(currentPageLabel) {
  return [
    BREADCRUMB_PATHS.dashboard,
    BREADCRUMB_PATHS.toolsHub,
    { label: currentPageLabel }
  ];
}

// Helper to build breadcrumbs for properties pages
export function buildPropertiesBreadcrumbs(currentPageLabel) {
  return [
    BREADCRUMB_PATHS.dashboard,
    BREADCRUMB_PATHS.propertiesHub,
    { label: currentPageLabel }
  ];
}

// Helper to build breadcrumbs for customers/loans pages
export function buildCustomersLoansBreadcrumbs(currentPageLabel) {
  return [
    BREADCRUMB_PATHS.dashboard,
    BREADCRUMB_PATHS.customersLoansHub,
    { label: currentPageLabel }
  ];
}

export default Breadcrumbs;
